import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Stack,
} from "@mui/material";
import MDButton from "components/MDButton";
import { useTranslation } from "react-i18next";

const reasonValues = [
  "hire",
  "promotion",
  "transfer",
  "raise",
  "demotion",
  "correction",
  "initial_setup",
];

// mode "assign": normal new-compensation flow, employee is pickable, POSTs
//   and auto-supersedes any existing active row.
// mode "correct": fixes a mistake on an existing row (correctingRecord).
//   Employee is fixed/read-only, all fields prefill from that row, and the
//   submit must NOT trigger supersede logic - the dialog just packages the
//   PUT payload, the caller (correctCompensation) hits the correction endpoint.
const AssignCompensationDialog = ({
  open,
  onClose,
  onSubmit,
  employees,
  payrollStructures,
  approvers,
  initialEmployeeId = "",
  mode = "assign",
  correctingRecord = null,
}) => {
  const { t } = useTranslation();
  const isCorrect = mode === "correct";

  const [employeeId, setEmployeeId] = useState(initialEmployeeId);
  const [payrollStructureId, setPayrollStructureId] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [reason, setReason] = useState("");
  const [approvedBy, setApprovedBy] = useState("");

  const selectedEmployee = employees.find((e) => e.empId === employeeId) || null;

  // Structures are built per-job, so only offer the ones linked to this
  // employee's job.
  const structuresForEmployee = selectedEmployee
    ? payrollStructures.filter((s) => s.jobId === selectedEmployee.jobId)
    : [];

  const selectedStructure = structuresForEmployee.find((s) => s.id === payrollStructureId) || null;

  const compensationReasons = reasonValues.map((value) => ({
    value,
    label: t(`admin.payroll.assignCompensation.reasons.${value}`),
  }));

  useEffect(() => {
    if (!open) {
      setEmployeeId("");
      setPayrollStructureId("");
      setEffectiveFrom("");
      setReason("");
      setApprovedBy("");
    } else if (isCorrect && correctingRecord) {
      setEmployeeId(correctingRecord.employeeId ?? "");
      setPayrollStructureId(correctingRecord.payrollStructureId ?? "");
      setEffectiveFrom(correctingRecord.effectiveFrom ?? "");
      setReason(correctingRecord.reason ?? "correction");
      setApprovedBy(correctingRecord.approvedBy ?? "");
    } else {
      setEmployeeId(initialEmployeeId);
    }
  }, [open]);

  const handleEmployeeChange = (event, newValue) => {
    setEmployeeId(newValue?.empId || "");
    // The previously chosen structure may not apply to the new employee's job
    setPayrollStructureId("");
  };

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = () => {
    const payload = {
      employeeId,
      payrollStructureId,
      currency: selectedStructure?.currency || "",
      effectiveFrom,
      reason,
      approvedBy: approvedBy || null,
    };
    // Correction PUTs go to /employee-compensation/{id} - the caller needs
    // the original row id to know which record to fix.
    onSubmit(isCorrect ? { ...payload, id: correctingRecord?.id } : payload);
  };

  const canSubmit = !!employeeId && !!payrollStructureId && !!effectiveFrom && !!reason;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: "bold" }}>
        {t(
          isCorrect
            ? "admin.payroll.assignCompensation.correctTitle"
            : "admin.payroll.assignCompensation.title"
        )}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Autocomplete
            options={employees}
            getOptionLabel={(employee) => employee.empName || ""}
            isOptionEqualToValue={(option, value) => option.empId === value.empId}
            value={selectedEmployee}
            onChange={handleEmployeeChange}
            disabled={isCorrect}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("admin.payroll.assignCompensation.employee")}
                helperText={isCorrect ? t("admin.payroll.assignCompensation.correctHelper") : ""}
              />
            )}
          />

          <Autocomplete
            options={structuresForEmployee}
            getOptionLabel={(structure) => structure.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedStructure}
            onChange={(event, value) => setPayrollStructureId(value?.id || "")}
            disabled={!selectedEmployee}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("admin.payroll.assignCompensation.structure")}
                helperText={
                  selectedEmployee && structuresForEmployee.length === 0
                    ? t("admin.payroll.assignCompensation.noStructuresForJob")
                    : ""
                }
              />
            )}
          />

          <TextField
            label={t("admin.payroll.assignCompensation.currency")}
            value={selectedStructure?.currency || ""}
            fullWidth
            disabled
            helperText={t("admin.payroll.assignCompensation.currencyHelper")}
          />

          <TextField
            label={t("admin.payroll.assignCompensation.effectiveFrom")}
            type="date"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          <Autocomplete
            options={compensationReasons}
            getOptionLabel={(reason) => reason.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            value={compensationReasons.find((r) => r.value === reason) || null}
            onChange={(event, value) => setReason(value?.value || "")}
            renderInput={(params) => (
              <TextField {...params} label={t("admin.payroll.assignCompensation.reason")} />
            )}
          />

          <Autocomplete
            options={approvers}
            getOptionLabel={(employee) => employee.empName || ""}
            isOptionEqualToValue={(option, value) => option.empId === value.empId}
            value={approvers.find((a) => a.empId === approvedBy) || null}
            onChange={(event, value) => setApprovedBy(value?.empId || "")}
            renderInput={(params) => (
              <TextField {...params} label={t("admin.payroll.assignCompensation.approvedBy")} />
            )}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton onClick={handleClose} color="secondary">
          {t("common.cancel")}
        </MDButton>

        <MDButton color="primary" onClick={handleSubmit} disabled={!canSubmit}>
          {t(isCorrect ? "common.save" : "common.assign")}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

AssignCompensationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  employees: PropTypes.array,
  payrollStructures: PropTypes.array,
  approvers: PropTypes.array,
  initialEmployeeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  mode: PropTypes.oneOf(["assign", "correct"]),
  correctingRecord: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    employeeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    payrollStructureId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    effectiveFrom: PropTypes.string,
    reason: PropTypes.string,
    approvedBy: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

AssignCompensationDialog.defaultProps = {
  employees: [],
  payrollStructures: [],
  approvers: [],
  initialEmployeeId: "",
  mode: "assign",
  correctingRecord: null,
};

export default AssignCompensationDialog;
